import { FunctionComponent, useEffect, useReducer, useState } from "react";
import { Helmet } from "react-helmet";
import { Card, FlagIcon, Text } from "rs-emd-ui-atoms";
import { ICountries, IOfflinePage } from "../../../models/offline-page.model";
import { ReactComponent as Logo } from "./../../../images/offline.svg";
import { ReactComponent as RSLogo } from "./../../../images/rs-logo.svg";
import { CountryDetails } from "./country-details-component/country-details-component";
import styles from "./offline-page-component.module.scss";

type OfflinePageProps = {
  data: IOfflinePage;
};

export enum OfflinePageActionType {
  ToggleCountryDetails = "ToggleCountryDetails",
  FilterCountries = "FilterCountries",
}
type Action =
  | {
      type: OfflinePageActionType.ToggleCountryDetails;
      countryChosen: string;
      continentChosen: string;
    }
  | {
      type: OfflinePageActionType.FilterCountries;
      hasCountriesChanged: boolean;
    };

type State = {
  isCountryDetailsOpen: boolean;
  continentChosen: string;
  countryChosen: string;
  hasCountriesChanged: boolean;
};

export const Offline: FunctionComponent<OfflinePageProps> = (
  props: OfflinePageProps
) => {
  const gtmId = process.env.REACT_APP_GTM_ID;
  const [continents] = useState<ICountries[]>(
    JSON.parse(JSON.stringify(props.data.continents))
  ); // deep copy to avoid issues when filtering
  const [searchString, setSearchString] = useState("");
  const [filteredList, setFilteredList] = useState<ICountries[]>(
    props.data.continents
  );

  useEffect(() => {
    if (searchString !== "")
      setFilteredList(
        continents.filter((continent) => {
          // use original data - props.data.continents
          let countries = props.data.continents.filter(
            (c) => c.continent === continent.continent
          )[0].countries;
          // filter by country name and assign to countries
          continent.countries = countries.filter((country) =>
            country.country
              .toLocaleLowerCase()
              .includes(searchString.toLowerCase())
          );
          // return true if any of the countries match the search string, this will remove any empty continents from the list
          return continent.countries.length > 0;
        })
      );
    else {
      setFilteredList(props.data.continents);
    }
  }, [searchString, props.data.continents, continents]);

  const [state, dispatch] = useReducer(reducer, {
    isCountryDetailsOpen: false,
    countryChosen: "",
    continentChosen: "",
    hasCountriesChanged: true,
  });

  return (
    <div className={`${styles["offline-page-wrapper"]}`}>
      <Helmet>
        {/* mk6e69 - Adobe Data Layer */}
        <script
          type="text/javascript"
          src="//nexus.ensighten.com/rscomponents/rsdeliversprod/Bootstrap.js"
        ></script>
        <script type="text/javascript">
          {`
            var rs = rs || {};
            rs.web = rs.web || {};
            rs.web.digitalData = rs.web.digitalData || {
                "store": "export",
                "breakpoint": "",
                "currencyCode": "GBP",
                "ecSystemId": "responsive",
                "error_type": "Technical error",
                "page_creation_date": "technical error page – 27/07/2021",
                "page_name": "offline page",
                "page_type": "errors",
                "site_Section": "Errors",
                "site_type": "responsive",
            };
         `}
        </script>
        {/* jn85tz - GA Data Layer */}
        <script type="text/javascript">
          {`
            (function(w, d, s, l, i) {
              w[l] = w[l] || [];
              w[l].push({
                  'gtm.start': new Date().getTime(),
                  event: 'gtm.js'
              });
              var f=d.getElementsByTagName(s)[0],
                  j=d.createElement(s),
                  dl=l != 'dataLayer' ? '&l=' + l : '';
              j.async=true;
              j.src='https://www.googletagmanager.com/gtm.js?id=' + i + dl;
              f.parentNode.insertBefore(j, f);
          })(window, document, 'script', 'dataLayer', 'GTM-${gtmId}');
         `}
        </script>
      </Helmet>

      {/* jn85tz - GA Data Layer */}
      <noscript>
        <iframe
          title="GTM"
          src={`https://www.googletagmanager.com/ns.html?id=GTM-${gtmId}`}
          height="0"
          width="0"
          style={{ display: "none", visibility: "hidden" }}
        ></iframe>
      </noscript>

      <Card className={`${styles["header-info"]}`}>
        <div className={`inner-page-wrapper ${styles["header-page-wrapper"]}`}>
          <div className={`${styles["div-rs-logo"]}`}>
            <RSLogo
              className={`${styles["rs-logo"]}`}
              title="RS logo"
              width={53}
              height={53}
            />
          </div>

          <div className={`${styles["div-offline-logo"]}`}>
            <Logo
              className={`${styles["offline-logo"]}`}
              title="Offline Logo"
            />
          </div>

          <div className={`inner-page-wrapper ${styles["div-detail"]}`}>
            <h1 className={`${styles["title"]}`}>
              RS Online is temporarily unavailable.
            </h1>
            <p className={`${styles["description"]}`}>
              Our website is currently unavailable to allow us to carry out
              improvements to the site. Please accept our apologies for the
              inconvenience. Click below to find out your local service details
              during this maintenance period.
            </p>

            <div className={styles["search-container"]}>
              <Text
                autoFocus={true}
                icon={"search"}
                value={searchString}
                onChange={(e) => setSearchString(e.target.value)}
                placeholder={"Search region"}
                className={`${styles["search-bar"]}`}
              ></Text>
            </div>
          </div>
        </div>
      </Card>

      <div className={`row ${styles.border}`}>
        <span className={styles.left}></span>
        <span className={styles.right}></span>
      </div>

      {state.hasCountriesChanged && (
        <div className={styles["div-country-row"]}>
          {filteredList?.map((p) => {
            return getContinents(p);
          })}
        </div>
      )}

      {state.isCountryDetailsOpen && (
        <CountryDetails
          labels={
            props.data.continents
              .filter((p) => p.continent === state.continentChosen)[0]
              ?.countries.filter((p) => p.country === state.countryChosen)[0]
              .labels
          }
          country={state.countryChosen}
          closeCallback={() =>
            dispatch({
              type: OfflinePageActionType.ToggleCountryDetails,
              continentChosen: "",
              countryChosen: "",
            })
          }
        ></CountryDetails>
      )}
    </div>
  );

  function getContinents(continent: ICountries) {
    const varContinent = continent.continent.toLowerCase().replace(" ", "-");
    return (
      <div>
        <div className={`inner-page-wrapper ${styles["div-countries"]}`}>
          <p className={styles["continent"]}>{continent.continent}</p>

          <div className={`${styles["div-country-row"]}`}>
            <div className={`${styles[varContinent]} ${styles["country-row"]}`}>
              {continent.countries
                .sort((a, b) => (a.country > b.country ? 1 : -1))
                .map((c) => {
                  return (
                    <div
                      onClick={() =>
                        handleClick(continent.continent, c.country)
                      }
                      className={styles["div-country"]}
                    >
                      <FlagIcon className={styles["flag"]} name={c.flagIcon} />
                      <p className={styles["country"]}>{c.country}</p>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  function handleClick(continent: string, country: string) {
    dispatch({
      type: OfflinePageActionType.ToggleCountryDetails,
      continentChosen: continent,
      countryChosen: country,
    });
  }
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case OfflinePageActionType.ToggleCountryDetails:
      return {
        ...state,
        continentChosen: action.continentChosen,
        countryChosen: action.countryChosen,
        isCountryDetailsOpen: !state.isCountryDetailsOpen,
      };
    case OfflinePageActionType.FilterCountries:
      return {
        ...state,
        hasCountriesChanged: true,
      };
  }
}
